import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, qs } from '../api';
import { Icon } from '../components/icons';
import { LanguagePicker } from '../components/LanguagePicker';
import { DeleteButton, Empty, ErrorBox, EventStatusBadge, Field, Loading, Modal, PageHead } from '../components/ui';
import { addDays, fmtDayShort, fmtTime, fromLocalInput, toLocalInput } from '../format';
import { useI18n } from '../i18n';
import type { Client, EventRow, EventStatus, Venue } from '../types';

type Range = 'upcoming' | 'past' | 'all';

export function EventsPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('upcoming');
  const [status, setStatus] = useState<EventStatus | ''>('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const now = useMemo(() => new Date(), []);
  const params = range === 'upcoming' ? { from: now.toISOString() } : range === 'past' ? { to: now.toISOString() } : {};
  const events = useQuery({
    queryKey: ['events', range, status],
    queryFn: () => api.get<EventRow[]>(`/events${qs({ ...params, status })}`),
  });
  const list = useMemo(() => {
    let l = events.data ?? [];
    if (search) {
      const s = search.toLowerCase();
      l = l.filter((e) => [e.title, e.venueName, e.clientName].some((x) => x?.toLowerCase().includes(s)));
    }
    return range === 'past' ? [...l].reverse() : l;
  }, [events.data, search, range]);

  return (
    <>
      <PageHead
        title={t('events.title')}
        sub={t('events.sub')}
        actions={
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <Icon.plus />
            {t('events.new')}
          </button>
        }
      />
      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('events.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('events.search')} />
        <select className="select" value={range} onChange={(e) => setRange(e.target.value as Range)} aria-label={t('events.range')}>
          <option value="upcoming">{t('events.upcoming')}</option>
          <option value="past">{t('events.past')}</option>
          <option value="all">{t('events.all')}</option>
        </select>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as EventStatus | '')} aria-label={t('common.status')}>
          <option value="">{t('equipment.allStatuses')}</option>
          {(['draft', 'confirmed', 'in_progress', 'completed', 'invoiced', 'cancelled'] as EventStatus[]).map((s) => (
            <option key={s} value={s}>{t(`eventStatus.${s}`)}</option>
          ))}
        </select>
      </div>
      <div className="card table-wrap">
        {events.isLoading ? (
          <Loading />
        ) : events.error ? (
          <div className="card-body"><ErrorBox error={events.error} /></div>
        ) : list.length === 0 ? (
          <Empty title={t('events.emptyTitle')} text={t('events.emptyText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('events.date')}</th>
                <th>{t('events.event')}</th>
                <th>{t('events.venue')}</th>
                <th>{t('events.client')}</th>
                <th className="right">{t('events.crew')}</th>
                <th className="right">{t('events.equipment')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="clickable" onClick={() => navigate(`/events/${e.id}`)}>
                  <td className="nowrap">
                    <strong>{fmtDayShort(e.setupStart, lang)}</strong>
                    <div className="small muted num">{fmtTime(e.setupStart, lang)}–{fmtTime(e.teardownEnd, lang)}</div>
                  </td>
                  <td>
                    <Link to={`/events/${e.id}`} onClick={(ev) => ev.stopPropagation()}><strong>{e.title}</strong></Link>
                    {e.requiresLead && <span className="badge" style={{ marginLeft: 6 }}>{t('events.leadRequired')}</span>}
                  </td>
                  <td>{e.venueName}</td>
                  <td className="muted">{e.clientName}</td>
                  <td className="right num">{e.crewCount}</td>
                  <td className="right num">{e.itemCount}</td>
                  <td><EventStatusBadge status={e.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && <EventFormModal onClose={() => setAdding(false)} />}
    </>
  );
}

function defaultTimes() {
  const d = addDays(new Date(), 1);
  const at = (h: number, m: number) => {
    const x = new Date(d);
    x.setHours(h, m, 0, 0);
    return toLocalInput(x.toISOString());
  };
  return { setupStart: at(7, 30), eventStart: at(9, 0), eventEnd: at(17, 30), teardownEnd: at(19, 0) };
}

export function EventFormModal({ event, onClose }: { event?: EventRow; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const venues = useQuery({ queryKey: ['venues'], queryFn: () => api.get<Venue[]>('/venues') });
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') });
  const init = event
    ? {
        setupStart: toLocalInput(event.setupStart),
        eventStart: toLocalInput(event.eventStart),
        eventEnd: toLocalInput(event.eventEnd),
        teardownEnd: toLocalInput(event.teardownEnd),
      }
    : defaultTimes();
  const [f, setF] = useState({
    title: event?.title ?? '',
    clientId: event?.clientId ?? '',
    venueId: event?.venueId ?? '',
    ...init,
    interpretation: event?.interpretation ?? false,
    languages: event?.languages ?? [],
    participants: event?.participants?.toString() ?? '',
    receivers: event ? event.receiversRequested !== null : false,
    receiversRequested: event?.receiversRequested?.toString() ?? '',
    requiresLead: event?.requiresLead ?? false,
    notes: event?.notes ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const missingLanguages = f.interpretation && f.languages.length === 0;
  const missingReceivers = f.receivers && !(Number(f.receiversRequested) > 0);
  // Interpreters assigned to this event are taken off it when interpretation is switched off.
  const dropsInterpreters = Boolean(event?.interpretation) && !f.interpretation;

  // Moving setup start moves the other times by the same amount, so a date change is one edit.
  const moveSetup = (v: string) => {
    if (!f.setupStart || !v) return setF({ ...f, setupStart: v });
    const delta = new Date(v).getTime() - new Date(f.setupStart).getTime();
    const shift = (x: string) => (x ? toLocalInput(new Date(new Date(x).getTime() + delta).toISOString()) : x);
    setF({ ...f, setupStart: v, eventStart: shift(f.eventStart), eventEnd: shift(f.eventEnd), teardownEnd: shift(f.teardownEnd) });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: f.title,
        clientId: f.clientId || null,
        venueId: f.venueId,
        setupStart: fromLocalInput(f.setupStart),
        eventStart: fromLocalInput(f.eventStart),
        eventEnd: fromLocalInput(f.eventEnd),
        teardownEnd: fromLocalInput(f.teardownEnd),
        interpretation: f.interpretation,
        languages: f.interpretation ? f.languages : [],
        participants: f.participants === '' ? null : Number(f.participants),
        receiversRequested: f.receivers && f.receiversRequested !== '' ? Number(f.receiversRequested) : null,
        requiresLead: f.requiresLead,
        notes: f.notes || null,
      };
      return event ? api.patch<EventRow & { warnings?: unknown }>(`/events/${event.id}`, body) : api.post<EventRow>('/events', body);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', saved.id] });
      qc.invalidateQueries({ queryKey: ['overview'] });
      onClose();
      if (!event) navigate(`/events/${saved.id}`);
    },
  });
  const noVenues = venues.data && venues.data.length === 0;
  return (
    <Modal
      wide
      title={event ? t('events.edit') : t('events.new')}
      onClose={onClose}
      footer={
        <>
          {event && (
            <DeleteButton
              name={event.title}
              path={`/events/${event.id}`}
              blockedCode="event_has_history"
              confirmText={t('del.eventText')}
              alternative={
                event.status === 'draft' || event.status === 'confirmed'
                  ? { label: t('del.cancelInstead'), run: () => api.post(`/events/${event.id}/status`, { status: 'cancelled' }) }
                  : undefined
              }
              onDone={() => {
                onClose();
                navigate('/events');
                qc.invalidateQueries();
              }}
            />
          )}
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !f.title || !f.venueId || missingLanguages || missingReceivers}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      {noVenues && (
        <div className="alert alert-warn">
          {t('events.noVenues')} <Link to="/venues">{t('nav.venues')} →</Link>
        </div>
      )}
      <div className="form-grid">
        <Field label={t('events.eventName')} full htmlFor="e-title">
          <input id="e-title" className="input" value={f.title} onChange={set('title')} autoFocus />
        </Field>
        <Field label={t('events.venue')} htmlFor="e-venue">
          <select id="e-venue" className="select" value={f.venueId} onChange={set('venueId')}>
            <option value="">{t('common.choose')}</option>
            {(venues.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label={t('events.client')} htmlFor="e-client">
          <select id="e-client" className="select" value={f.clientId} onChange={set('clientId')}>
            <option value="">{t('common.none')}</option>
            {(clients.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label={t('events.setupStart')} hint={t('events.setupStartHint')} htmlFor="e-ss">
          <input id="e-ss" className="input" type="datetime-local" value={f.setupStart} onChange={(e) => moveSetup(e.target.value)} />
        </Field>
        <Field label={t('events.eventStart')} htmlFor="e-es">
          <input id="e-es" className="input" type="datetime-local" value={f.eventStart} onChange={set('eventStart')} />
        </Field>
        <Field label={t('events.eventEnd')} htmlFor="e-ee">
          <input id="e-ee" className="input" type="datetime-local" value={f.eventEnd} onChange={set('eventEnd')} />
        </Field>
        <Field label={t('events.teardownEnd')} htmlFor="e-te">
          <input id="e-te" className="input" type="datetime-local" value={f.teardownEnd} onChange={set('teardownEnd')} />
        </Field>
        <Field label={t('events.participants')} hint={t('events.participantsHint')} htmlFor="e-part">
          <input id="e-part" className="input" type="number" min="0" inputMode="numeric" value={f.participants} onChange={set('participants')} />
        </Field>
        <div className="full stack" style={{ gap: 8 }}>
          <label className="checkbox">
            <input type="checkbox" checked={f.interpretation} onChange={(e) => setF({ ...f, interpretation: e.target.checked })} />
            <strong>{t('events.interpretation')}</strong>
          </label>
          {f.interpretation && (
            <div className="sub-fields">
              <Field label={t('events.languagesPick')} hint={t('events.languagesPickHint')}>
                <LanguagePicker value={f.languages} onChange={(languages) => setF({ ...f, languages })} />
              </Field>
            </div>
          )}
          {dropsInterpreters && <div className="alert alert-warn">{t('events.interpretationOffWarn')}</div>}
        </div>
        <div className="full stack" style={{ gap: 8 }}>
          <label className="checkbox">
            <input type="checkbox" checked={f.receivers} onChange={(e) => setF({ ...f, receivers: e.target.checked })} />
            <strong>{t('events.receivers')}</strong>
          </label>
          {f.receivers && (
            <div className="sub-fields">
              <Field label={t('events.receiversCount')} hint={t('events.receiversHint')} htmlFor="e-rcv">
                <input id="e-rcv" className="input" style={{ maxWidth: 180 }} type="number" min="1" inputMode="numeric" value={f.receiversRequested} onChange={set('receiversRequested')} />
              </Field>
            </div>
          )}
        </div>
        <div className="full">
          <label className="checkbox">
            <input type="checkbox" checked={f.requiresLead} onChange={(e) => setF({ ...f, requiresLead: e.target.checked })} />
            {t('events.requiresLead')}
          </label>
        </div>
        <Field label={t('common.notes')} full htmlFor="e-notes">
          <textarea id="e-notes" className="textarea" value={f.notes} onChange={set('notes')} placeholder={t('events.notesPlaceholder')} />
        </Field>
      </div>
    </Modal>
  );
}
